import React from "react";
import { Link } from "react-router-dom";

const stats = [
  { value: "12+", label: "Professional Tools", sub: "Finance, analytics, marketing & more" },
  { value: "6", label: "Tool Categories", sub: "Built for every workflow stage" },
  { value: "100%", label: "Free to Start", sub: "No credit card, no lock-in" },
  { value: "0", label: "Data Sold", sub: "We never sell or share your inputs" },
];

const values = [
  {
    icon: "🎯",
    name: "Precision Over Noise",
    desc: "Every feature we ship solves a real problem. We ruthlessly cut features that add complexity without value. Good tools do one thing exceptionally well.",
  },
  {
    icon: "🔒",
    name: "Privacy by Default",
    desc: "We don't monetize your data. We don't run ads. Your inputs belong to you. Security and transparency are structural commitments, not marketing language.",
  },
  {
    icon: "⚡",
    name: "Performance as Respect",
    desc: "Your time matters. Every tool loads in under 2 seconds. We build on edge infrastructure so latency doesn't become an obstacle between you and your work.",
  },
  {
    icon: "🌍",
    name: "Accessibility for Everyone",
    desc: "Professional-grade tools shouldn't require enterprise contracts. Our Free tier is genuinely useful, and we price thoughtfully for individuals and small teams.",
  },
];

const techStack = [
  { name: "Cloudflare Workers", badge: "Edge Compute", desc: "Sub-50ms global latency via 200+ edge locations" },
  { name: "React 18 + TypeScript", badge: "Frontend", desc: "Type-safe, component-driven UI architecture" },
  { name: "Cloudflare D1", badge: "Database", desc: "Globally replicated SQLite at the edge" },
  { name: "Cloudflare KV", badge: "Sessions", desc: "Low-latency key-value for auth and caching" },
  { name: "Cloudflare R2", badge: "Storage", desc: "S3-compatible object storage for files" },
  { name: "Stripe", badge: "Payments", desc: "PCI DSS Level 1 — we never see your card data" },
];

const milestones = [
  { year: "2023", title: "Platform Founded", desc: "9nd.org launched with 6 core tools and a conviction: professional tools should be accessible to everyone." },
  { year: "2024", title: "10 Tools & Pro Tier", desc: "Expanded to 10 tools across 6 categories. Launched Pro subscription with API access and saved runs." },
  { year: "2025", title: "Budget Planner Launch", desc: "Released the Budget Planner — a comprehensive 11-mode personal finance system with household sync and cloud persistence." },
  { year: "2025", title: "Consulting Practice", desc: "Opened an expert consulting arm to help teams evaluate, integrate, and scale their digital tool stack." },
];

export default function About() {
  return (
    <div className="min-h-screen bg-white">

      {/* ── Hero ── */}
      <section className="bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 text-white py-24">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-indigo-300 font-semibold uppercase tracking-widest text-sm mb-5">About 9nd.org</p>
          <h1 className="text-5xl font-extrabold leading-tight mb-6">
            Professional-grade tools.<br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-300 to-purple-300">
              Built for people, not enterprises.
            </span>
          </h1>
          <p className="text-xl text-slate-300 max-w-2xl mx-auto leading-relaxed">
            We believe the best tools in the world shouldn't cost a fortune or require a procurement team.
            9nd.org exists to put powerful, precise, privacy-first tools in the hands of every professional.
          </p>
        </div>
      </section>

      {/* ── Stats block ── */}
      <section className="border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 py-10 grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((s) => (
            <div key={s.label} className="text-center py-4">
              <p className="text-4xl font-extrabold text-indigo-600 tabular-nums">{s.value}</p>
              <p className="font-bold text-gray-900 mt-1 text-sm">{s.label}</p>
              <p className="text-xs text-gray-500 mt-0.5">{s.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── Mission ── */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-4 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm mb-4">Our Mission</p>
            <h2 className="text-3xl font-bold text-gray-900 mb-5 leading-snug">
              Expertise shouldn't be gated by budget.
            </h2>
            <p className="text-gray-600 leading-relaxed mb-5">
              The tools that power large organizations are often inaccessible to everyone else — locked behind enterprise licenses,
              complex onboarding, and sales calls. We built 9nd.org to close that gap.
            </p>
            <p className="text-gray-600 leading-relaxed mb-5">
              Every tool on our platform is designed with the same rigor you'd expect from enterprise software — but structured for
              the individual, the freelancer, the small team, and the growing company who doesn't have an IT department.
            </p>
            <p className="text-gray-600 leading-relaxed">
              Our consulting practice extends that same philosophy: bringing structured, enterprise-quality thinking to engagements
              of any size, without the enterprise overhead.
            </p>
          </div>
          <div className="space-y-4">
            {[
              { label: "Finance Tools", detail: "Budgeting, forecasting, debt payoff, retirement planning" },
              { label: "Analytics Tools", detail: "Portfolio risk, data modeling, decision frameworks" },
              { label: "Marketing Tools", detail: "Color palettes, font pairings, copy generators" },
              { label: "Productivity Tools", detail: "Document summarization, workflow automation" },
              { label: "Development Tools", detail: "Code generation, schema builders, API utilities" },
              { label: "Consulting", detail: "Strategy, implementation, and advisory engagements" },
            ].map((item) => (
              <div key={item.label} className="flex items-start gap-3 p-4 border border-gray-100 rounded-xl hover:border-indigo-200 transition">
                <span className="mt-0.5 shrink-0 w-5 h-5 rounded-full bg-indigo-100 text-indigo-600 flex items-center justify-center text-xs font-bold">✓</span>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{item.label}</p>
                  <p className="text-xs text-gray-500 mt-0.5">{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Values ── */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Our Values</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">What we stand for</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {values.map((v) => (
              <div key={v.name} className="bg-white border border-gray-100 rounded-2xl p-7 shadow-sm hover:shadow-md transition">
                <div className="text-3xl mb-4">{v.icon}</div>
                <h3 className="text-lg font-bold text-gray-900 mb-3">{v.name}</h3>
                <p className="text-gray-600 text-sm leading-relaxed">{v.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Timeline / Story ── */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Our Story</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">How we got here</h2>
          <div className="space-y-0">
            {milestones.map((m, i) => (
              <div key={i} className="flex gap-6">
                <div className="flex flex-col items-center">
                  <div className="w-10 h-10 rounded-full bg-indigo-600 text-white flex items-center justify-center font-bold text-xs shrink-0">
                    {m.year.slice(-2)}
                  </div>
                  {i < milestones.length - 1 && <div className="w-0.5 bg-indigo-100 flex-1 my-2" />}
                </div>
                <div className="pb-10">
                  <span className="text-xs font-bold text-indigo-500 uppercase tracking-widest">{m.year}</span>
                  <h3 className="font-bold text-gray-900 mt-1 mb-2">{m.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Founder Quote ── */}
      <section className="py-16 bg-gradient-to-r from-indigo-600 to-purple-700 text-white">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <p className="text-5xl font-serif text-indigo-200 mb-4">"</p>
          <blockquote className="text-xl font-medium leading-relaxed mb-6">
            The tools you rely on every day shape the quality of every decision you make. We set out to build the tools we wished existed — fast, private, and genuinely useful without a price tag that assumes you work for a Fortune 500.
          </blockquote>
          <p className="text-indigo-200 font-semibold">Founder, 9nd.org</p>
        </div>
      </section>

      {/* ── Tech Stack ── */}
      <section className="py-20 bg-white">
        <div className="max-w-5xl mx-auto px-4">
          <p className="text-indigo-600 font-semibold uppercase tracking-widest text-sm text-center mb-4">Infrastructure</p>
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-4">Built on trusted technology</h2>
          <p className="text-gray-500 text-center max-w-xl mx-auto mb-12">
            We chose each component of our stack for reliability, security, and performance — not cost-cutting or convenience.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {techStack.map((t) => (
              <div key={t.name} className="border border-gray-100 rounded-2xl p-5 hover:border-indigo-200 transition shadow-sm">
                <div className="flex items-center gap-2 mb-3">
                  <p className="font-bold text-gray-900 text-sm">{t.name}</p>
                  <span className="text-xs bg-indigo-50 text-indigo-600 px-2 py-0.5 rounded-full font-medium">{t.badge}</span>
                </div>
                <p className="text-gray-500 text-xs leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link to="/trust" className="inline-block text-sm font-semibold text-indigo-600 hover:text-indigo-800 underline underline-offset-2">
              Read our full Trust & Security page →
            </Link>
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-gray-50 text-center border-t border-gray-100">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-gray-900 mb-4">Ready to get started?</h2>
          <p className="text-gray-500 mb-8">Explore our tools free, or talk to us about a consulting engagement.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link
              to="/register"
              className="inline-block bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold px-8 py-4 rounded-xl hover:opacity-90 transition shadow-md"
            >
              Start Free →
            </Link>
            <Link
              to="/consulting"
              className="inline-block border border-gray-300 text-gray-700 font-semibold px-8 py-4 rounded-xl hover:border-indigo-300 hover:text-indigo-700 transition"
            >
              Consulting Services
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}